import { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import {
  Box,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  Tab,
  Tabs,
  Typography
} from "@mui/material";

import { useTesterIdentity } from "./TesterIdentityProvider";
import { isAuthErrorCode } from "./authErrors";
import SessionExpiredPanel from "./SessionExpiredPanel";

const formatRelative = (timestamp) => {
  if (!timestamp?.toMillis) {
    return "";
  }
  const diffMs = Date.now() - timestamp.toMillis();
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) {
    return "just now";
  }
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const QUESTION_STATUS_DISPLAY = {
  investigating: { label: "Investigating", color: "info" },
  answered: { label: "Answered", color: "success" },
  failed: { label: "Failed", color: "error" },
  "rate-limited": { label: "Rate-limited", color: "warning" },
  closed: { label: "Closed", color: "default" }
};

const getDisplay = (status) =>
  QUESTION_STATUS_DISPLAY[status] || {
    label: status || "unknown",
    color: "default"
  };

const isClosed = (q) => q.status === "closed";

const QuestionList = ({ selectedQuestionId, onSelect }) => {
  const { internalDb, tester } = useTesterIdentity();
  const [questions, setQuestions] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [errorCode, setErrorCode] = useState(null);
  const [tab, setTab] = useState("open");
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    if (!internalDb || !tester?.id) {
      setQuestions([]);
      setLoaded(false);
      setErrorCode(null);
      return undefined;
    }
    const qref = query(
      collection(internalDb, "tester-questions"),
      where("testerId", "==", tester.id)
    );
    const unsubscribe = onSnapshot(
      qref,
      (snap) => {
        const docs = snap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }));
        const sorted = docs.sort(
          (a, b) =>
            (b.askedAt?.toMillis?.() || 0) - (a.askedAt?.toMillis?.() || 0)
        );
        setQuestions(sorted);
        setErrorCode(null);
        setLoaded(true);
      },
      (err) => {
        console.warn("[tester-chat] questions subscription error:", err.message);
        setQuestions([]);
        setErrorCode(err.code || "unknown");
        setLoaded(true);
      }
    );
    return unsubscribe;
  }, [internalDb, tester?.id, retryToken]);

  const openQuestions = useMemo(
    () => questions.filter((q) => !isClosed(q)),
    [questions]
  );
  const closedQuestions = useMemo(() => questions.filter(isClosed), [questions]);
  const shown = tab === "open" ? openQuestions : closedQuestions;

  if (!loaded) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="textSecondary">
          Loading your questions…
        </Typography>
      </Box>
    );
  }

  if (isAuthErrorCode(errorCode)) {
    return (
      <SessionExpiredPanel
        message="Your tester session expired. Sign in again to load your questions."
        onReauthed={() => setRetryToken((prev) => prev + 1)}
      />
    );
  }

  if (errorCode) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="error" gutterBottom>
          Something went wrong loading your questions.
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Refresh the page to retry.
        </Typography>
      </Box>
    );
  }

  if (questions.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Curious how something works? Click &quot;Ask about the code&quot; up top.
        </Typography>
        <Typography variant="body2" color="textSecondary">
          The agent will read the relevant code and report back in ~2–3 min.
          Useful for targeting your testing before you start poking around.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Tabs
        value={tab}
        onChange={(_, next) => setTab(next)}
        variant="fullWidth"
        sx={{ borderBottom: 1, borderColor: "divider", minHeight: 36 }}
      >
        <Tab
          value="open"
          label={`Open${openQuestions.length ? ` (${openQuestions.length})` : ""}`}
          sx={{ minHeight: 36, py: 0.5 }}
        />
        <Tab
          value="closed"
          label={`Closed${closedQuestions.length ? ` (${closedQuestions.length})` : ""}`}
          sx={{ minHeight: 36, py: 0.5 }}
        />
      </Tabs>
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        {shown.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="textSecondary">
              {tab === "open"
                ? "No open questions. Click \"Ask about the code\" to start one."
                : "No closed questions yet."}
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding>
            {shown.map((q) => {
              const { label, color } = getDisplay(q.status);
              return (
                <ListItemButton
                  key={q.id}
                  selected={q.id === selectedQuestionId}
                  onClick={() => onSelect(q.id)}
                  divider
                >
                  <ListItemText
                    primary={
                      <Typography variant="body2" noWrap>
                        {q.question || "(no question)"}
                      </Typography>
                    }
                    secondary={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mt: 0.5
                        }}
                      >
                        <Chip label={label} size="small" color={color} />
                        <Typography variant="caption" color="textSecondary">
                          {formatRelative(q.askedAt)}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Box>
    </Box>
  );
};

export default QuestionList;
